import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import SearchBar from './components/SearchBar';
import CategoryCard from './components/CategoryAccordion';
import SearchResultItem from './components/SearchResultItem';
import Footer from './components/Footer';
import AgendaBanner from './components/AgendaBanner';
import WhatsAppButton from './components/WhatsAppButton';
import calzData from './data/calzData.json';
import InstitutionalButton from './components/InstitutionalButton';

function App() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCategories, setFilteredCategories] = useState(calzData);
  const [flatSearchResults, setFlatSearchResults] = useState([]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCategories(calzData);
      setFlatSearchResults([]);
      return;
    }

    // Remove accents and lowercase
    const normalizedQuery = searchQuery.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const searchTerms = normalizedQuery.split(/\s+/).filter(Boolean);

    // Create a flat list of ALL individual items that match, ignoring their parent categories.
    const results = [];

    calzData.forEach(category => {
      // Para búsquedas precisas, priorizamos revisar los ítems en sí mismos.
      if (category.links) {
        category.links.forEach(link => {
          // Normalize item title and text
          const itemString = (link.title + " " + (link.text || "")).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

          // Check if EVERY word typed by the user exists somewhere in the item text
          const itemMatch = searchTerms.every(term => itemString.includes(term));

          if (itemMatch) {
            results.push({
              ...link,
              categoryName: category.category,
              id: `${category.id}-${link.title}` // unique key
            });
          }
        });
      }
    });

    setFlatSearchResults(results);
  }, [searchQuery]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 font-sans text-slate-800">
      <Header />

      <main className="flex-grow container mx-auto px-4 py-12 max-w-6xl space-y-8">
        {/* Sección de Búsqueda con efecto de solapado profesional */}
        <div className="flex flex-col items-center -mt-20 md:-mt-24 relative z-40 px-4">
          <div className="w-full max-w-3xl bg-white/95 backdrop-blur-md p-1.5 rounded-2xl shadow-2xl border border-white/20">
            <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </div>
        </div>

        {/* Dynamic Display based on Search State */}
        <div className="mt-12">
          {searchQuery.trim() !== '' ? (
            /* MODO BÚSQUEDA ACTIVO: MUESTRA ITEMS PUNTUALES */
            flatSearchResults.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl shadow-sm border border-slate-100 max-w-2xl mx-auto">
                <svg className="w-20 h-20 mx-auto text-slate-200 mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-2xl text-slate-600 font-medium">No encontramos resultados</p>
                <p className="text-slate-400 mt-3 text-lg">Intenta con otros términos referidos a tu búsqueda.</p>
              </div>
            ) : (
              <div>
                <h3 className="text-xl font-bold text-slate-700 mb-6 border-b border-slate-200 pb-2">
                  Resultados para "{searchQuery}"
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {flatSearchResults.map(result => (
                    <SearchResultItem key={result.id} result={result} />
                  ))}
                </div>
              </div>
            )
          ) : (
            /* MODO NORMAL: MUESTRA TARJETAS DE CATEGORÍAS */
            <div className="flex flex-wrap justify-center gap-6">
              {filteredCategories.map(category => (
                <div key={category.id} className="w-full sm:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-1.5rem)] xl:w-[calc(25%-1.5rem)] flex justify-center">
                  <div className="w-full h-full">
                    <CategoryCard categoryData={category} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Agenda Banner at the bottom of main */}
        {!searchQuery.trim() && (
          <>
            <AgendaBanner />
            <InstitutionalButton />
          </>
        )}
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}

export default App;